import { useEffect } from 'react'
import { useIsRestoring, useQueryClient } from '@tanstack/react-query'
import { useSession } from '@/admin/hooks/use-session'
import { useOrg } from '@/admin/hooks/use-org'
import { usePermissions } from '@/admin/hooks/use-permissions'
import {
  productTemplatesQueryOptions,
  publishedProductsQueryOptions,
  holdingFormSchemaQueryOptions,
} from '@/admin/hooks/use-products'
import { registrationSettingsQueryOptions } from '@/admin/hooks/use-registration-settings'
import { registrationFieldsQueryOptions } from '@/admin/hooks/use-registration-fields'
import { entityTypesQueryOptions } from '@/admin/hooks/use-entities'
import {
  claimTypeOptionsQueryOptions,
  claimDisclaimersQueryOptions,
  multiItemClaimTypesQueryOptions,
  createClaimMetadataQueryOptions,
  type ClaimTypeOption,
} from '@/admin/hooks/use-claims'

/**
 * Run a callback when the browser is idle, falling back to a short timeout on
 * engines without ``requestIdleCallback`` (Safari). Returns a cancel handle.
 */
function scheduleIdle(cb: () => void): () => void {
  const w = window as typeof window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
    cancelIdleCallback?: (handle: number) => void
  }
  if (typeof w.requestIdleCallback === 'function') {
    const handle = w.requestIdleCallback(cb, { timeout: 3000 })
    return () => w.cancelIdleCallback?.(handle)
  }
  const handle = window.setTimeout(cb, 1200)
  return () => window.clearTimeout(handle)
}

/**
 * The claim types whose create metadata is worth warming: the ones a user can
 * pick. An inactive type shows as "Coming Soon" and may have no schema yet, so
 * warming it would only request metadata that does not exist.
 */
export function claimTypesToWarm(options: ClaimTypeOption[]): string[] {
  return options.filter((option) => option.active).map((option) => option.value)
}

/**
 * Background cache warmer for the registration and claim wizards.
 *
 * After login (session + org resolved) and once the persisted IndexedDB cache
 * has finished hydrating, this prefetches the wizards' tenant-global, bounded
 * metadata during browser idle time so the first wizard open paints from cache
 * instead of blanking while it fetches.
 *
 * Design notes:
 * - Only parameter-free / per-template queries are warmed. Per-product warranty
 *   availability and per-holding data are intentionally NOT warmed - they are
 *   unbounded and genuinely vary per instance.
 * - Both wizards are warmed for every authenticated user. The backend endpoints
 *   are themselves feature-flag gated (a disabled feature 404s), and every warm
 *   is best-effort, so warming a feature a tenant does not use is a harmless,
 *   swallowed no-op rather than something to gate on the client.
 * - ``prefetchQuery`` respects ``staleTime``: if the persisted cache restored a
 *   fresh copy, warming is a no-op (no wasted request). Everything runs
 *   best-effort - failures are swallowed so a warm never surfaces an error.
 * - Mounted once at the authenticated-route boundary (``ProtectedRoute``), so it
 *   covers both fresh logins and returning/reloaded sessions.
 */
export function useWarmWizardCache(): void {
  const queryClient = useQueryClient()
  const { data: session } = useSession()
  const { orgUuid, isLoading: orgLoading } = useOrg()
  const isRestoring = useIsRestoring()
  // A prefetch bypasses a hook's `enabled`, so the warmer has to carry the
  // same permission gate itself or it re-fires the 403 the hook now avoids.
  const { can } = usePermissions()
  const canReadProductTemplates = can('product-templates.view')
  const canReadClaims =
    can('claims.view.global') || can('claims.view.acl')

  const authed = Boolean(session?.is_authenticated)

  useEffect(() => {
    // Wait until the cache has hydrated and we have a resolved, authenticated
    // org context - otherwise we would fetch under the wrong partition or
    // race the restore gate.
    if (isRestoring || !authed || orgLoading || !orgUuid) return

    let cancelled = false

    const warmRegistration = async () => {
      await Promise.allSettled([
        queryClient.prefetchQuery(registrationSettingsQueryOptions()),
        queryClient.prefetchQuery(registrationFieldsQueryOptions()),
        // Prewarm the EXACT key the wizard's product picker fetches on
        // open: bounded first page (300) with variants included. Passing
        // the wrong params here (e.g. the default page_size/exclude
        // variants) produces a different query hash, so the warm silently
        // misses and the picker blanks - the bug this signature fixes.
        queryClient.prefetchQuery(
          publishedProductsQueryOptions({
            include_variants: true,
            page_size: '300',
          }),
        ),
        queryClient.prefetchQuery(entityTypesQueryOptions({ key: 'holding' })),
      ])
      // Per-template form schema - bounded (a tenant has a handful of
      // product templates) and cached per template, so this is what makes the
      // registration wizard's fields/host/purchase steps paint instantly.
      const templates = canReadProductTemplates
        ? await queryClient
            .ensureQueryData(productTemplatesQueryOptions())
            .catch(() => [])
        : []
      if (cancelled) return
      await Promise.allSettled(
        (templates ?? []).map((t) =>
          queryClient.prefetchQuery(holdingFormSchemaQueryOptions(t.uuid)),
        ),
      )
    }

    const warmClaims = async () => {
      await Promise.allSettled([
        queryClient.prefetchQuery(claimDisclaimersQueryOptions()),
        queryClient.prefetchQuery(multiItemClaimTypesQueryOptions()),
      ])
      // Per-claim-type create metadata - one entry per claim schema key. The
      // product-template narrowing of ``product_impacted`` options resolves on
      // holding selection; the base (template-less) metadata is what the
      // wizard needs to render the details step.
      const claimTypes = await queryClient
        .ensureQueryData(claimTypeOptionsQueryOptions())
        .catch(() => ({ options: [] }))
      if (cancelled) return
      await Promise.allSettled(
        claimTypesToWarm(claimTypes?.options ?? []).map((key) =>
          queryClient.prefetchQuery(createClaimMetadataQueryOptions(key)),
        ),
      )
    }

    const run = () => {
      if (cancelled) return
      // Shared by both wizards.
      if (canReadProductTemplates) {
        queryClient.prefetchQuery(productTemplatesQueryOptions()).catch(() => {})
      }
      warmRegistration().catch(() => {})
      if (canReadClaims) {
        warmClaims().catch(() => {})
      }
    }

    const cancelIdle = scheduleIdle(run)
    return () => {
      cancelled = true
      cancelIdle()
    }
  }, [
    queryClient,
    isRestoring,
    authed,
    orgLoading,
    orgUuid,
    canReadProductTemplates,
    canReadClaims,
  ])
}
